import { usePost } from "@albertine/api-client";
import {
    isSupportedChatUploadMimeType,
    normalizeUploadFileName,
    normalizeUploadMimeType,
} from "@albertine/chat-uploads";
import type { InitiateClientFileUploadData } from "@albertine/types/openapi/lmt";
import { useCallback, useMemo } from "react";

function assertClientUpload(
    data: InitiateClientFileUploadData | { source: string },
): asserts data is InitiateClientFileUploadData & { source: "client" } {
    if (data.source !== "client") {
        throw new Error("Expected a client file upload");
    }
}

export function useUploadChatAttachmentFiles() {
    const { mutateAsync: initiateLmtFileUpload } = usePost("initiateLmtFileUpload");
    const { mutateAsync: finalizeLmtFileUpload } = usePost("finalizeLmtFileUpload");
    const uploadFile = useCallback(
        async (file: File, memberId: string): Promise<string> => {
            const mimeType = normalizeUploadMimeType(file.type);
            const fileName = normalizeUploadFileName(file.name, mimeType);
            const initiated = await initiateLmtFileUpload({
                json: {
                    source: "client",
                    uploadPurpose: {
                        type: "chat-message-attachment",
                        memberId,
                    },
                    mimeType,
                    fileName,
                },
            });
            assertClientUpload(initiated);
            const transfer = await fetch(initiated.uploadUrl, {
                method: initiated.uploadHttpMethod,
                headers: initiated.uploadHeaders,
                body: file,
            });
            if (!transfer.ok) {
                throw new Error(`Uploading the attachment failed (${transfer.status})`);
            }
            const finalized = await finalizeLmtFileUpload({
                param: { id: initiated.id },
            });
            if (finalized.status === "rejected") {
                throw new Error("The attachment was rejected");
            }
            return initiated.id;
        },
        [initiateLmtFileUpload, finalizeLmtFileUpload],
    );

    const uploadAttachmentFiles = useCallback(
        async (files: File[], memberId: string): Promise<string[]> => {
            const ids: string[] = [];
            for (const file of files) {
                ids.push(await uploadFile(file, memberId));
            }
            return ids;
        },
        [uploadFile],
    );
    return useMemo(
        () => ({
            uploadFile,
            uploadAttachmentFiles,
        }),
        [uploadFile, uploadAttachmentFiles],
    );
}
